import { useEffect, useState } from "react";
import { Select, SimpleGrid } from "@mantine/core";
import { config } from "../utils/config";

interface WritingOptionsSelectorProps {
  disabled?: boolean;
  onChange?: (options: WritingOptions) => void;
}

export interface WritingOptions {
  character: string | null;
  tone: string | null;
  style: string | null;
  format: string | null;
}

export function WritingOptionsSelector({ 
  disabled = false,
  onChange
}: WritingOptionsSelectorProps) {
  const [character, setCharacter] = useState<string | null>(null);
  const [tone, setTone] = useState<string | null>(null);
  const [style, setStyle] = useState<string | null>(null);
  const [format, setFormat] = useState<string | null>(null);

  // Load writing options from local storage
  useEffect(() => {
    try {
      const storedOptions = localStorage.getItem("writing-options");
      if (storedOptions) {
        const options = JSON.parse(storedOptions) as WritingOptions;
        setCharacter(options.character);
        setTone(options.tone);
        setStyle(options.style);
        setFormat(options.format);
      }
    } catch (error) {
      console.error("Failed to load writing options:", error);
    }
  }, []);

  // Save writing options to local storage and notify parent
  const saveOptions = (options: WritingOptions) => {
    try {
      localStorage.setItem("writing-options", JSON.stringify(options));
      if (onChange) {
        onChange(options);
      }
    } catch (error) {
      console.error("Failed to save writing options:", error);
    }
  };

  // Handle character change
  const handleCharacterChange = (value: string | null) => {
    setCharacter(value);
    saveOptions({
      character: value,
      tone,
      style,
      format
    });
  };

  // Handle tone change
  const handleToneChange = (value: string | null) => {
    setTone(value);
    saveOptions({
      character,
      tone: value,
      style,
      format
    });
  };

  // Handle style change
  const handleStyleChange = (value: string | null) => {
    setStyle(value);
    saveOptions({
      character,
      tone,
      style: value,
      format
    });
  };

  // Handle format change
  const handleFormatChange = (value: string | null) => {
    setFormat(value);
    saveOptions({
      character,
      tone,
      style,
      format: value
    });
  };

  return (
    <SimpleGrid
      mb="sm"
      spacing="xs"
      breakpoints={[
        { minWidth: "sm", cols: 4 },
        { maxWidth: "sm", cols: 2 },
      ]}
    >
      <Select
        value={character}
        onChange={handleCharacterChange}
        data={config.writingCharacters}
        placeholder="Character"
        variant="filled"
        searchable
        clearable
        sx={{ flex: 1 }}
        disabled={disabled}
      />
      <Select
        value={tone}
        onChange={handleToneChange}
        data={config.writingTones}
        placeholder="Tone"
        variant="filled"
        searchable
        clearable
        sx={{ flex: 1 }}
        disabled={disabled}
      />
      <Select
        value={style}
        onChange={handleStyleChange}
        data={config.writingStyles}
        placeholder="Style"
        variant="filled"
        searchable
        clearable
        sx={{ flex: 1 }}
        disabled={disabled}
      />
      <Select
        value={format}
        onChange={handleFormatChange}
        data={config.writingFormats}
        placeholder="Format"
        variant="filled"
        searchable
        clearable
        sx={{ flex: 1 }}
        disabled={disabled}
      />
    </SimpleGrid>
  );
}
